import {
  TranscriptElement,
  TranscriptTextElement,
} from '../../types.ts/transcript';
import { VideoElement } from './elements/VideoElement';
import { Timeline } from './Timeline';
import {
  InferredTranscript,
  InferredTranscriptElement,
} from './InferredTranscript';

/**
 * Type for transcript modifications
 */
export type TranscriptModification = {
  id: string; // ID of the transcript element being modified
  type: 'replaceText' | 'mute' | 'addPunctuation' | 'removePunctuation'; // Type of modification
  value: any; // New value (e.g., replacement text, boolean for mute)
};

/**
 * Transcript class that manages the original transcript and active transcript derivation
 */
export class Transcript {
  /**
   * The original, immutable transcript elements
   * This is never modified directly - it serves as a reference point
   */
  private originalTranscription: readonly TranscriptElement[];

  /**
   * Array of modifications to apply to the transcript elements
   * These are overlaid on the active transcript without changing the original
   */
  private transcriptModifications: TranscriptModification[];

  /**
   * The inference helper for generating comprehensive transcript state
   */
  private inferredTranscriptHelper: InferredTranscript;

  /**
   * Creates a new Transcript instance
   * @param original The original transcript elements
   */
  constructor(original: TranscriptElement[]) {
    // Create a deep copy and assign word_index to text elements
    let wordCounter = 0;
    const updatedTranscription = original.map((element, idx) => {
      if (element.type === 'text') {
        // Use existing word_index if present, otherwise assign new one
        const wordIndex = (element as any).word_index ?? wordCounter++;
        return {
          ...element,
          // Truncate ts and end_ts to 4 decimal places since Creatomate hard-chops
          ts: Math.round(element.ts * 10000) / 10000,
          end_ts: Math.round(element.end_ts * 10000) / 10000,
          initial_index: idx,
          word_index: wordIndex, // TODO: Deprecate this in favor of initial_index
        };
      }
      return { ...element }; // Ensure deep copy for all elements
    });
    this.originalTranscription = Object.freeze(updatedTranscription);
    this.transcriptModifications = [];

    // Initialize the inferred transcript helper
    this.inferredTranscriptHelper = new InferredTranscript(
      this.originalTranscription,
    );
  }

  /**
   * Get word_index from initial_index array. Hack for existing transcript functions.
   * @param startIndex The start index of the word in the original transcript
   * @param The end index of the word in the original transcript
   * @returns Array<string> of word indexes
   */
  getWordIndexFromIndexes(startIndex: number, endIndex: number): string[] {
    const wordIndexes: string[] = [];
    for (let i = startIndex; i <= endIndex; i++) {
      const element = this.originalTranscription[i];
      if (element.type === 'text' && element.word_index !== undefined) {
        wordIndexes.push(element.word_index.toString());
      }
    }
    return wordIndexes;
  }

  /**
   * Gets the original transcript elements
   * @returns The original transcript elements (immutable)
   */
  getOriginalTranscription(): ReadonlyArray<TranscriptElement> {
    return this.originalTranscription;
  }

  /**
   * Gets the active transcript based on video elements
   * @param videoElements Array of video elements to determine active transcript.
   * @returns Array of active transcript elements with modifications applied
   */
  getActiveTranscript(videoElements: VideoElement[]): TranscriptElement[] {
    const sortedElements = videoElements.sort((a, b) => a.time - b.time);

    const activeWords = sortedElements.flatMap((ve: VideoElement) => {
      const seg = ve.getTranscriptSegment();
      return seg?.getWordsWithTimestamps() || [];
    });

    const activeWordsWithPunctuation =
      this.includeRelevantPunctuation(activeWords);
    return this.applyModifications(activeWordsWithPunctuation);
  }

  /**
   * Infers a full transcript based on a timeline, showing which parts of the original transcript
   * are included or removed in the current edit
   *
   * @param timeline The timeline containing video elements
   * @returns Array of transcript elements with status property indicating if each element
   * is included in the timeline (status='included') or was removed (status='removed')
   */
  inferFullTranscript(timeline: Timeline): InferredTranscriptElement[] {
    return this.inferredTranscriptHelper.inferFullTranscript(timeline);
  }

  /**
   * Determines if a word is in the active transcript
   * @param wordId The word_index of the word to check
   * @param activeTranscript The current active transcript
   * @returns Boolean indicating if the word is in the active transcript
   */
  isWordInActiveTranscript(
    wordId: string,
    activeTranscript: TranscriptElement[],
  ): boolean {
    return activeTranscript.some(
      (word) =>
        word.type === 'text' &&
        word.word_index !== undefined &&
        word.word_index.toString() === wordId,
    );
  }

  /**
   * Determines if a word can be removed
   * @param wordId The word_index of the word to check
   * @param activeTranscript The current active transcript
   * @returns Boolean indicating if the word can be removed
   */
  canWordBeRemoved(
    wordId: string,
    activeTranscript: TranscriptElement[],
  ): boolean {
    // A word can be removed if it's in the active transcript
    return this.isWordInActiveTranscript(wordId, activeTranscript);
  }

  /**
   * Determines if a word can be restored
   * @param wordId The word_index of the word to check
   * @param activeTranscript The current active transcript
   * @returns Boolean indicating if the word can be restored
   */
  canWordBeRestored(
    wordId: string,
    activeTranscript: TranscriptElement[],
  ): boolean {
    // A word can be restored if it's in the original transcript but not in the active transcript
    const originalElement = this.getElementByWordIndex(wordId);
    if (!originalElement) return false;

    return !this.isWordInActiveTranscript(wordId, activeTranscript);
  }

  /**
   * Adds a modification to the transcript
   * @param modification The modification to add
   */
  addModification(modification: TranscriptModification): void {
    // Check if there's an existing modification for this element
    const existingIndex = this.transcriptModifications.findIndex(
      (mod) => mod.id === modification.id && mod.type === modification.type,
    );

    if (existingIndex !== -1) {
      // Replace the existing modification
      this.transcriptModifications[existingIndex] = modification;
    } else {
      // Add a new modification
      this.transcriptModifications.push(modification);
    }
  }

  /**
   * Removes a modification
   * @param id ID of the transcript element
   * @param type Type of modification to remove
   * @returns true if a modification was removed, false otherwise
   */
  removeModification(
    id: string,
    type: TranscriptModification['type'],
  ): boolean {
    const initialLength = this.transcriptModifications.length;
    this.transcriptModifications = this.transcriptModifications.filter(
      (mod) => !(mod.id === id && mod.type === type),
    );
    return initialLength > this.transcriptModifications.length;
  }

  /**
   * Gets all modifications
   * @returns Array of transcript modifications
   */
  getModifications(): TranscriptModification[] {
    return [...this.transcriptModifications];
  }

  /**
   * Clears all modifications
   */
  clearModifications(): void {
    this.transcriptModifications = [];
  }

  /**
   * Includes punctuation elements that are associated with active words
   */
  private includeRelevantPunctuation(
    activeWords: TranscriptElement[],
  ): TranscriptElement[] {
    if (activeWords.length === 0) return [];

    // Get the indices of active words in the original transcription
    const activeIndices = activeWords.map((word) => {
      return this.originalTranscription.findIndex(
        (el) =>
          el.type === word.type &&
          el.value === word.value &&
          (el.type !== 'text' ||
            (el as TranscriptTextElement).ts ===
              (word as TranscriptTextElement).ts),
      );
    });

    // Find punctuation elements that should be included
    const result: TranscriptElement[] = [];

    activeWords.forEach((word, idx) => {
      result.push(word);

      // Check if there's punctuation following this word
      const wordIndex = activeIndices[idx];
      if (
        wordIndex !== -1 &&
        wordIndex + 1 < this.originalTranscription.length
      ) {
        const nextElement = this.originalTranscription[wordIndex + 1];

        // If the next element is punctuation, include it if this isn't the last word
        // or if the next element is also part of our active words
        if (
          nextElement.type === 'punct' &&
          (idx < activeWords.length - 1 ||
            activeIndices.includes(wordIndex + 1))
        ) {
          result.push(nextElement);
        }
      }
    });

    return result;
  }

  /**
   * Applies modifications to transcript elements
   */
  private applyModifications(
    elements: TranscriptElement[],
  ): TranscriptElement[] {
    // Create a deep copy so we can modify the elements
    const modifiedElements = JSON.parse(
      JSON.stringify(elements),
    ) as TranscriptElement[];

    // Apply each modification
    for (const element of modifiedElements) {
      // Find all modifications for this element - using ONLY word_index
      const mods = this.transcriptModifications.filter((mod) => {
        // Only match on word_index, never on initial_index
        const wordIdMatch =
          element.type === 'text' &&
          element.word_index !== undefined &&
          mod.id === element.word_index.toString();

        if (wordIdMatch) {
          return true;
        }

        return false;
      });

      for (const mod of mods) {
        switch (mod.type) {
          case 'replaceText':
            // Replace the text value
            if (element.type === 'text' || element.type === 'punct') {
              element.old_value = element.value;
              element.value = mod.value;
              element.state = 'replaced';
            }
            break;

          case 'mute':
            // Set muted state
            if (element.type === 'text') {
              if (mod.value === true) {
                element.state = 'muted';
              } else if (element.state === 'muted') {
                // Only clear the state if it was muted
                element.state = undefined;
              }
            }
            break;

          case 'addPunctuation':
            // This would typically be handled by adding a new element
            // but is not directly applicable to the current element
            break;

          case 'removePunctuation':
            // Mark punctuation for removal
            if (element.type === 'punct') {
              element.state = 'removed';
            }
            break;
        }
      }
    }

    // Filter out elements marked as removed
    return modifiedElements.filter((el) => el.state !== 'removed');
  }

  /**
   * Gets a transcript element by ID
   * @param id ID of the element to find
   * @returns The transcript element, or undefined if not found
   */
  getElementById(id: string): TranscriptElement | undefined {
    return this.originalTranscription.find(
      (el) => el.initial_index.toString() === id,
    );
  }

  /**
   * Gets a transcript element by word_index
   * @param wordIndex The word_index to find
   * @returns The transcript element, or undefined if not found
   */
  getElementByWordIndex(wordIndex: string): TranscriptElement | undefined {
    return this.originalTranscription.find(
      (el) => el.type === 'text' && el.word_index?.toString() === wordIndex,
    );
  }
}
